import type { TenantUsage } from '@order-pilot/shared';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/Badge';
import { SectionCard } from '@/components/SectionCard';
import { EmptyState, Skeleton } from '@/components/States';
import { fetchTenantUsage, usageKeys } from '@/features/plans/api';

/** One measured figure against its ceiling. */
function LimitRow({
  label,
  actual,
  limit,
  over,
}: {
  label: string;
  actual: string;
  limit: number | null;
  over: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3 last:border-b-0">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="flex items-center gap-2 text-sm tabular-nums">
        <span className={over ? 'font-semibold text-amber-800' : 'text-slate-900'}>
          {actual}
        </span>
        <span className="text-slate-400">
          {/* A null ceiling is "No limit" — never a blank, which would read as
              missing data, and never 0, which would read as "none allowed". */}
          / {limit === null ? 'No limit' : limit.toLocaleString()}
        </span>
        {over ? <Badge tone="warning">Over</Badge> : null}
      </span>
    </div>
  );
}

function UsageBody({ usage }: { usage: TenantUsage }) {
  if (usage.plan === null) {
    return (
      <EmptyState
        title="No plan assigned"
        description="This tenant is not on a plan, so nothing is measured against a limit. Assign one from Edit tenant."
        compact
      />
    );
  }

  return (
    <div>
      <LimitRow
        label="Average bills per day"
        // One decimal: a 30-day average is genuinely fractional — 3 bills in
        // 30 days is 0.1/day, and rounding that to 0 would read as no trade.
        actual={usage.avgBillsPerDay.toFixed(1)}
        limit={usage.plan.maxBillsPerDay}
        over={usage.overBills}
      />
      <LimitRow
        label="Branches"
        actual={usage.branchCount.toString()}
        limit={usage.plan.maxBranches}
        over={usage.overBranches}
      />
      <LimitRow
        label="Tills"
        actual={usage.deviceCount.toString()}
        limit={usage.plan.maxDevices}
        over={usage.overDevices}
      />
    </div>
  );
}

/**
 * What this tenant is using, against the plan they are on.
 *
 * Nothing here blocks the tenant: an "Over" badge is a prompt to have a
 * pricing conversation, not a state that degrades their service.
 */
export function PlanUsageSection({ tenantId }: { tenantId: string }) {
  const query = useQuery({
    queryKey: usageKeys.tenant(tenantId),
    queryFn: ({ signal }) => fetchTenantUsage(tenantId, signal),
  });

  const price =
    query.data?.plan?.pricePerMonth === null ||
    query.data?.plan?.pricePerMonth === undefined
      ? null
      : `${query.data.plan.currency} ${query.data.plan.pricePerMonth}`;

  return (
    <SectionCard
      headingId="plan-usage"
      title="Plan"
      description={
        query.data?.plan
          ? `${query.data.plan.name}${price ? ` · ${price} / month` : ' · price negotiated'} · measured over the last 30 days`
          : 'Usage against the tenant’s subscription tier.'
      }
    >
      {query.isPending ? (
        <div className="space-y-2 px-5 py-4">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : query.isError || query.data === undefined ? (
        <EmptyState
          title="Could not load usage"
          description="The plan is still assigned; only this measurement failed to load."
          compact
        />
      ) : (
        <UsageBody usage={query.data} />
      )}
    </SectionCard>
  );
}
